import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useUser } from '../../../contexts/UserContext'
import { userService } from '../../../services/userService'
import OnboardingHeader from '../OnboardingHeader'

interface OnboardingStepCompleteProps {
  onClose: () => void
}

const OnboardingStepComplete: React.FC<OnboardingStepCompleteProps> = ({
  onClose,
}) => {
  const { user, refreshUser } = useUser()
  const [error, setError] = useState<string | null>(null)
  const [hasAttempted, setHasAttempted] = useState(false)

  useEffect(() => {
    const completeOnboarding = async () => {
      // Don't run if no user, already completed, or already attempted (including on error)
      if (!user || user.has_completed_contractor_onboarding || hasAttempted)
        return

      setHasAttempted(true)

      try {
        await userService.completeContractorOnboarding(user.id)
        // Refresh user context to get updated has_completed_contractor_onboarding flag
        await refreshUser()
      } catch (err) {
        console.error('Failed to complete contractor onboarding:', err)
        setError(
          err instanceof Error ? err.message : 'Failed to complete onboarding',
        )
      }
    }

    completeOnboarding()
  }, [user, refreshUser, hasAttempted])

  return (
    <div className={css(styles.root)}>
      <div className={css(styles.content)}>
        <OnboardingHeader title="Review and Complete" progress={100} />
        <img
          src="/all_set.png"
          alt="All set"
          className={css(styles.allSetImg)}
        />
        <h1 className={css(styles.title)}>All set!</h1>
        <p className={css(styles.subtitle)}>
          Your contractor profile has been created successfully.
          <br />
          You're ready to start collaborating.
        </p>
        {error && <p className={css(styles.error)}>{error}</p>}
      </div>
      <div className={css(styles.buttonRow)}>
        <button
          className={css(styles.primaryButton)}
          onClick={onClose}
          disabled={!!error}
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  allSetImg: {
    width: 80,
    height: 80,
    objectFit: 'contain',
    marginBottom: 8,
  },
  title: {
    fontSize: 28,
    fontWeight: 600,
    color: '#1F3A8A',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    color: '#222',
    marginBottom: 32,
    textAlign: 'center',
  },
  buttonRow: {
    display: 'flex',
    width: '100%',
    marginTop: 32,
    justifyContent: 'center',
  },
  error: {
    color: '#DC2626',
    fontSize: 14,
    marginTop: 8,
    textAlign: 'center',
  },
  primaryButton: {
    background: '#2563EB',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    width: '50%',
    fontSize: 16,
    fontWeight: 600,
    margin: '0 auto',
    cursor: 'pointer',
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
    ':disabled': {
      background: '#93C5FD',
      cursor: 'not-allowed',
    },
  },
})

export default OnboardingStepComplete
